import { usePage, router } from '@inertiajs/react'
import { AlertCircle, CheckCircle2, Clock, FileText, Eye, Download } from 'lucide-react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { DocumentPreviewModal } from '@/components/shared/DocumentPreviewModal'
import { Button } from '@/components/ui/button'
import { DynamicForm } from '@/components/applicant/DynamicForm'
import { DocumentUploader } from '@/components/shared/DocumentUploader'
import { BusinessTaskDetailed, FieldDefinition, FieldValue, DocumentAttachment, DashboardPageProps, TaskTypeSummary } from '@/types'
import { StatusBadge } from '@/components/shared/StatusBadge'

function getFieldValueDisplay(field: FieldDefinition, fieldValues: FieldValue[]): string {
  const fv = fieldValues.find(v => v.field_id === field.id)
  if (!fv) return '—'
  if (fv.option_labels && fv.option_labels.length > 0) return fv.option_labels.join(', ')
  if (fv.value != null) return String(fv.value)
  return '—'
}

type Props = DashboardPageProps & {
  task: BusinessTaskDetailed
  type: TaskTypeSummary
  fields: FieldDefinition[]
  field_values: FieldValue[]
  documents: DocumentAttachment[]
  templates: DocumentAttachment[]
}

export default function BusinessTasksShow() {
  const { task, fields, field_values, documents, templates, auth } = usePage<Props>().props
  const slug = auth.account?.slug

  return (
    <DashboardLayout
      title={task.type_name}
      breadcrumbs={[
        { label: auth.account?.name || 'Workspace', href: `/${slug}/dashboard` },
        { label: 'Tasks', href: `/${slug}/business_tasks` },
        { label: task.type_name },
      ]}
    >
      <div className="space-y-6">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-xl font-semibold tracking-tight">{task.type_name}</h1>
            <StatusBadge status={task.status} />
          </div>
          <p className="text-sm text-muted-foreground">{task.business_name}</p>
        </div>

        {task.due_date && (
          <div className={`flex items-center gap-2 text-sm ${task.overdue ? 'text-red-600 font-medium' : 'text-muted-foreground'}`}>
            {task.overdue ? <AlertCircle className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
            Due: {new Date(task.due_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </div>
        )}

        {task.instructions && (
          <section className="border rounded-lg">
            <div className="px-4 py-3 border-b">
              <h2 className="text-sm font-medium text-foreground">Instructions</h2>
            </div>
            <div className="px-4 py-4">
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">{task.instructions}</p>
            </div>
          </section>
        )}

        {task.type_description && (
          <section className="border rounded-lg">
            <div className="px-4 py-3 border-b">
              <h2 className="text-sm font-medium text-foreground">Task Description</h2>
            </div>
            <div className="px-4 py-4">
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">{task.type_description}</p>
            </div>
          </section>
        )}

        {templates.length > 0 && (
          <section className="border rounded-lg">
            <div className="px-4 py-3 border-b">
              <h2 className="text-sm font-medium text-foreground">Reference Documents</h2>
            </div>
            <div className="px-4 py-3">
              {templates.map(doc => (
                <div key={doc.id} className="flex items-center justify-between py-1">
                  <div className="flex items-center gap-2">
                    <FileText className="w-3.5 h-3.5 text-muted-foreground" />
                    <span className="text-sm">{doc.name}</span>
                  </div>
                  {doc.file_url && (
                    <div className="flex items-center gap-3">
                      <DocumentPreviewModal 
                        url={doc.file_url} 
                        filename={doc.name || 'Document'}
                        trigger={
                          <button className="inline-flex items-center gap-1 text-xs text-primary hover:underline cursor-pointer">
                            <Eye className="w-3 h-3" />
                            View
                          </button>
                        }
                      />
                      <a href={doc.file_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
                        <Download className="w-3 h-3" />
                        Download
                      </a>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {task.status === 'pending' && fields.length > 0 && (
          <section className="border rounded-lg">
            <div className="px-4 py-3 border-b">
              <h2 className="text-sm font-medium text-foreground">Your Response</h2>
            </div>
            <div className="px-4 py-4">
              <DynamicForm
                fields={fields}
                valuesApiPath={`/${slug}/business_tasks/${task.id}/response`}
                submitLabel="Submit Response"
              />
            </div>
          </section>
        )}

        {field_values.length > 0 && fields.length > 0 && (
          <section className="border rounded-lg">
            <div className="px-4 py-3 border-b">
              <h2 className="text-sm font-medium text-foreground flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                Your Response
              </h2>
            </div>
            <div className="px-4 py-4 space-y-3">
              {fields.map(field => (
                <div key={field.id} className="flex justify-between items-start text-sm">
                  <span className="text-muted-foreground">{field.label}</span>
                  <span className="text-right max-w-[60%]">{getFieldValueDisplay(field, field_values)}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        {task.status === 'completed' && (
          <section className="border rounded-lg bg-green-50/50">
            <div className="px-4 py-3">
              <div className="flex items-center gap-2 text-green-700">
                <CheckCircle2 className="w-4 h-4" />
                <span className="text-sm font-medium">Task Completed</span>
              </div>
              {task.completed_at && <p className="text-xs text-green-600 mt-1">Completed on {new Date(task.completed_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>}
              {task.completion_notes && <p className="text-sm text-green-700 mt-2">{task.completion_notes}</p>}
            </div>
          </section>
        )}

        {task.status === 'pending' && fields.length === 0 && (
          <section className="border rounded-lg">
            <div className="px-4 py-3 border-b">
              <h2 className="text-sm font-medium text-foreground">Action Required</h2>
            </div>
            <div className="px-4 py-4">
              <p className="text-sm text-muted-foreground mb-4">Please click below when you have completed this task's requirements (e.g. uploading documents).</p>
              <Button onClick={() => router.post(`/${slug}/business_tasks/${task.id}/response`)}>
                Mark as Done
              </Button>
            </div>
          </section>
        )}

        {documents.length > 0 && task.status !== 'pending' && (
          <section className="border rounded-lg">
            <div className="px-4 py-3 border-b">
              <h2 className="text-sm font-medium text-foreground">Documents</h2>
            </div>
            <div className="px-4 py-3">
              {documents.map(doc => (
                <div key={doc.id} className="flex items-center justify-between py-1">
                  <div className="flex items-center gap-2">
                    <FileText className="w-3.5 h-3.5 text-muted-foreground" />
                    <span className="text-sm">{doc.name}</span>
                  </div>
                  {doc.file_url && (
                    <div className="flex items-center gap-3">
                      <DocumentPreviewModal 
                        url={doc.file_url} 
                        filename={doc.name || 'Document'}
                        trigger={
                          <button className="inline-flex items-center gap-1 text-xs text-primary hover:underline cursor-pointer">
                            <Eye className="w-3 h-3" />
                            View
                          </button>
                        }
                      />
                      <a href={doc.file_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
                        <Download className="w-3 h-3" />
                        Download
                      </a>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {task.status === 'pending' && (
          <DocumentUploader
            title="Documents"
            documents={documents}
            uploadPath={`/${slug}/business_tasks/${task.id}/documents`}
            deletePath={(docId) => `/${slug}/business_tasks/${task.id}/documents/${docId}`}
          />
        )}
      </div>
    </DashboardLayout>
  )
}
